/**
 * useEmailLinkedLead — Iter 156.9. Custom hook extracted from
 * `EmailLinkedLeadPanel.jsx` (455 lines, Iter 145). Owns all stateful
 * concerns of the right-rail linked-lead panel:
 *
 *   • lead loading + refresh
 *   • label add/remove (optimistic w/ rollback)
 *   • customer-details form + save + place-order
 *   • record-existing-order
 *   • AI runs (suggest / extract / sentiment / categorize)
 *   • product-code lookup with suggestion fallback
 *   • price-scope (INR vs USD) derived from lead phone prefix
 *
 * Returning a flat object that the JSX-only panel destructures. Keeps
 * the visual component small and testable in isolation.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { leadsApi, aiApi, labelsApi, booksApi } from "../../lib/api";

export default function useEmailLinkedLead(leadId) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allLabels, setAllLabels] = useState([]);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newLabelText, setNewLabelText] = useState("");
  // Customer-details form (shared with Order tab)
  const [custForm, setCustForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    customer_address: "", customer_pincode: "",
    notes: "", amount: "", payment_mode: "COD",
  });
  const [savingCust, setSavingCust] = useState(false);
  // Record-an-existing-order form (Order tab bottom)
  const [orderNum, setOrderNum] = useState("");
  const [orderAmt, setOrderAmt] = useState("");
  // AI buttons
  const [aiLoading, setAiLoading] = useState(null);
  // Product lookup
  const [productLookup, setProductLookup] = useState("");
  const [productLoading, setProductLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState(null);

  // Price scope for product lookup card. INR for India + neighbouring
  // SAARC mobile prefixes, USD otherwise.
  const priceScope = (() => {
    const ph = String((lead?.phone) || "").replace(/\D/g, "");
    if (!ph) return "INR";
    if (ph.startsWith("91") || ph.startsWith("977") || ph.startsWith("94") || ph.startsWith("880")) return "INR";
    return "USD";
  })();

  // ── Lead refresh ────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [r, lblR] = await Promise.allSettled([
        leadsApi.getOne(leadId),
        labelsApi.getAll(),
      ]);
      if (r.status === "fulfilled") {
        const ld = r.value.data;
        setLead(ld);
        setCustForm((prev) => ({
          ...prev,
          customer_name: ld.customer_name || ld.name || "",
          customer_email: ld.customer_email || "",
          customer_phone: ld.customer_phone || ld.phone || "",
          customer_address: ld.customer_address || "",
          customer_pincode: ld.customer_pincode || "",
          notes: ld.notes || "",
          amount: ld.amount ? String(ld.amount) : "",
          payment_mode: ld.payment_mode || "COD",
        }));
      }
      if (lblR.status === "fulfilled") {
        setAllLabels((lblR.value.data || []).map((x) => x.name || x.label || x).filter(Boolean));
      }
    } catch (e) {
      toast.error("Failed to load linked lead");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Labels (optimistic w/ rollback) ─────────────────────────────────
  const onAddLabel = useCallback(async (text) => {
    const t = (text || "").trim();
    if (!t || !lead) return;
    if ((lead.labels || []).includes(t)) {
      setShowLabelInput(false);
      setNewLabelText("");
      return;
    }
    const next = [...(lead.labels || []), t];
    setLead((p) => ({ ...p, labels: next }));
    setNewLabelText("");
    setShowLabelInput(false);
    try {
      await leadsApi.updateLabels(lead.id, next);
      toast.success(`Added "${t}"`);
    } catch {
      toast.error("Failed to add label");
      setLead((p) => ({ ...p, labels: lead.labels || [] }));
    }
  }, [lead]);

  const onRemoveLabel = useCallback(async (l) => {
    if (!lead) return;
    const next = (lead.labels || []).filter((x) => x !== l);
    setLead((p) => ({ ...p, labels: next }));
    try {
      await leadsApi.updateLabels(lead.id, next);
    } catch {
      toast.error("Failed to remove label");
      setLead((p) => ({ ...p, labels: lead.labels || [] }));
    }
  }, [lead]);

  // ── Customer form save + order actions ──────────────────────────────
  const onSaveCust = useCallback(async () => {
    if (!lead) return;
    setSavingCust(true);
    try {
      await leadsApi.updateCustomerDetails(lead.id, custForm);
      toast.success("Customer details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCust(false);
    }
  }, [lead, custForm]);

  const onPlaceOrder = useCallback(async () => {
    if (!lead) return;
    setSavingCust(true);
    try {
      const res = await leadsApi.placeAdminOrder(lead.id, custForm);
      toast.success(`Order placed: ${res.data.order_number || "OK"}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Place order failed");
    } finally {
      setSavingCust(false);
    }
  }, [lead, custForm, refresh]);

  const onRecordOrder = useCallback(async () => {
    if (!lead || !orderNum.trim()) return;
    try {
      await leadsApi.recordOrder(lead.id, {
        order_number: orderNum.trim(),
        amount: parseFloat(orderAmt) || 0,
      });
      toast.success("Order recorded");
      setOrderNum(""); setOrderAmt("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Record failed");
    }
  }, [lead, orderNum, orderAmt, refresh]);

  // ── AI ──────────────────────────────────────────────────────────────
  const runAi = useCallback(async (kind, fn) => {
    if (!lead) return;
    setAiLoading(kind);
    try {
      const r = await fn(lead.id);
      if (kind === "extract" && r?.data?.details) {
        setCustForm((p) => ({ ...p, ...r.data.details }));
        toast.success("Customer details auto-filled");
      } else if (kind === "sentiment" && r?.data?.sentiment) {
        setLead((p) => ({ ...p, sentiment: r.data.sentiment }));
        toast.success(`Sentiment: ${r.data.sentiment}`);
      } else if (kind === "categorize" && r?.data?.category) {
        setLead((p) => ({ ...p, category: r.data.category }));
        toast.success(`Category: ${r.data.category}`);
      } else if (kind === "suggest" && r?.data?.suggestion) {
        navigator.clipboard.writeText(r.data.suggestion);
        toast.success("Suggested reply copied — paste into your email");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind} failed`);
    } finally {
      setAiLoading(null);
    }
  }, [lead]);

  // ── Product lookup ──────────────────────────────────────────────────
  const lookupProduct = useCallback(async (overrideCode) => {
    const code = (typeof overrideCode === "string" ? overrideCode : productLookup || "").trim();
    if (!code) return;
    setProductLoading(true);
    setProductData(null);
    setProductSuggestions(null);
    try {
      const r = await booksApi.lookup(code);
      setProductData(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.suggestions)) {
        setProductSuggestions(detail.suggestions);
      } else {
        const msg = (typeof detail === "string") ? detail : (detail?.message || "Product not found");
        toast.error(msg);
        setProductSuggestions([]);
      }
    } finally {
      setProductLoading(false);
    }
  }, [productLookup]);

  const buildProductDetailsMessage = useCallback((pd) => {
    if (!pd?.raw) return "";
    const det = pd.raw.details || {};
    const lines = [];
    if (det.title) lines.push(`*${det.title}*`);
    if (pd.itemcode) lines.push(`Item code: ${pd.itemcode}`);
    if (det.author) lines.push(`Author: ${det.author}`);
    if (det.publisher) lines.push(`Publisher: ${det.publisher}`);
    if (det.pages) lines.push(`Pages: ${det.pages}`);
    if (det.language) lines.push(`Language: ${det.language}`);
    if (det.binding) lines.push(`Binding: ${det.binding}`);
    if (det.isbn) lines.push(`ISBN: ${det.isbn}`);
    return lines.join("\n");
  }, []);

  const copyProductSummary = useCallback(() => {
    if (!productData?.raw) return;
    navigator.clipboard.writeText(buildProductDetailsMessage(productData));
    toast.success("Product summary copied — paste into reply");
  }, [productData, buildProductDetailsMessage]);

  return {
    // state
    lead, loading, allLabels,
    showLabelInput, setShowLabelInput, newLabelText, setNewLabelText,
    custForm, setCustForm, savingCust,
    orderNum, setOrderNum, orderAmt, setOrderAmt,
    aiLoading,
    productLookup, setProductLookup, productLoading, productData, setProductData,
    productSuggestions, setProductSuggestions,
    priceScope,
    // actions
    refresh,
    onAddLabel, onRemoveLabel,
    onSaveCust, onPlaceOrder, onRecordOrder,
    runAi, runExtract: () => runAi("extract", aiApi.extractDetails),
    runSuggest: () => runAi("suggest", aiApi.suggestReply),
    runSentiment: () => runAi("sentiment", aiApi.detectSentiment),
    runCategorize: () => runAi("categorize", aiApi.smartCategorize),
    lookupProduct, buildProductDetailsMessage, copyProductSummary,
  };
}
